/**
 * @fileoverview DictationButton — the one mic button for text fields
 * (MOR-1456).
 *
 * Mirrors the notes editor's toolbar toggle (same labels, same three
 * states) so dictation reads the same wherever it appears: mic when idle,
 * a stop square while listening, a spinner while the final transcript is
 * being produced. Pair it with `useDictatedField`.
 */

import { Loader2, Mic, Square } from "lucide-react";

import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives";
import {
  isDictationControlShown,
  type ChatVoiceInput,
} from "@/ui/hooks/use-chat-voice-input";

/**
 * Renders the mic toggle for a `useDictatedField` (or any `ChatVoiceInput`).
 * `voice.isListening` selects the stop state and `voice.isProcessing` the
 * disabled spinner state; otherwise it offers to start. Clicking calls
 * `voice.toggle`, which opens or closes the session. Renders nothing when
 * the deployment has dictation off (`voice.isEnabled` false) and no session
 * is open: an open session keeps its stop control
 * (`isDictationControlShown`).
 */
export function DictationButton({
  voice,
  className,
}: {
  voice: Pick<
    ChatVoiceInput,
    "isListening" | "isProcessing" | "toggle" | "isEnabled"
  >;
  className?: string;
}) {
  const { isListening, isProcessing, toggle } = voice;
  if (!isDictationControlShown(voice)) return null;
  const label = isListening
    ? "Stop dictation"
    : isProcessing
      ? "Finalising transcript"
      : "Start dictation";
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      aria-pressed={isListening}
      disabled={isProcessing}
      title={
        isProcessing
          ? "Finalising transcript…"
          : isListening
            ? "Stop dictation"
            : "Dictate via voice (auto-stops after 5 minutes)"
      }
      className={cn(
        "h-7 w-7 shrink-0 text-muted-foreground",
        isListening &&
          "bg-destructive/10 text-destructive hover:bg-destructive/20",
        isProcessing &&
          "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
        className,
      )}
      onClick={toggle}
    >
      {isListening ? (
        <Square className="h-3 w-3 fill-current" />
      ) : isProcessing ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Mic className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}
